<!--*
 * Component to take pictures for questions that require it
 *
 * @author   pablo.barco
 * @date     2020-07-08
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <div class="row">
    <div class="col--12">
      <div class="row" v-if="!viewMode">
        <div class="col--12">
          <div class="take-picture text-center mx-4">
            <app-button class="col--auto offset--auto" btn-type="solid" @click="clickFileInput" enter-to-tab>
              <img src="../../../assets/images/camera-white.svg" width="20" height="20" class="mr-2">
              {{ $t('surveyQuestionScreen.takeAPicture.label') }}
            </app-button>
            <input ref="fileInputImage" hidden type="file" capture="environment" @change="addFiles" accept="image/*">
            <input ref="fileInputVideo" hidden type="file" capture="environment" @change="addFiles" accept="video/*">
            <input ref="fileInputAny" hidden type="file" multiple @change="addFiles" accept="*">
          </div>
        </div>
      </div>
      <div v-if="showInputTypeSelector" class="row mt-4">
        <div class="col--12">
          <div class="mx-4">
            <div class="row">
              <div v-if="showUploadVideoOption" class="col--4 p-0">
                <div class="text-center" @click="captureVideo">
                  <app-button class="col--auto offset--auto" btn-type="ghost" enter-to-tab>
                    <img src="../../../assets/images/video-purple.svg" width="20" height="20" class="mr-2">
                    {{ $t('surveyQuestionScreen.takeAPicture.uploadVideo') }}
                  </app-button>
                </div>
              </div>
              <div v-if="showUploadPictureOption" class="col--4 p-0">
                <div class="text-center" @click="captureImage">
                  <app-button class="col--auto offset--auto" btn-type="ghost" enter-to-tab>
                    <img src="../../../assets/images/camera-purple.svg" width="22" height="22" class="mr-2">
                    {{ $t('surveyQuestionScreen.takeAPicture.uploadPicture') }}
                  </app-button>
                </div>
              </div>
              <div v-show="showUploadFileOption" class="col--4 p-0">
                <div class="text-center" @click="captureFile">
                  <app-button class="col--auto offset--auto" btn-type="ghost" layout="icon-label" icon="upload" enter-to-tab>
                    {{ $t('surveyQuestionScreen.takeAPicture.uploadFile') }}
                  </app-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="converted.length > 0 && !converted.some((file) => file.deleted)" class="row m-4 text-center">
        <div class="col--sm--12 col--md--6 px-0 px--md-3" v-for="(file, index) in converted" :key="'file' + index">
          <div v-if="file.deleted === false && (file.base64 || file.src)" :ref="`fileNameRow-${file.name}`"
               class="m-0 my-3 align-items-center" :class="{ 'deleted-Style': file.deleted, 'image-item': !viewMode, 'image-item-disabled': viewMode }"
               @click="showPreview(file)">
            <div class="row">
              <div class="col" v-if="isImage(getFileExtension(file.name))">
                <img class="img-fluid" :src="file.base64 ? file.base64 : file.src" :alt="file.name"/>
              </div>
              <div class="col" v-else-if="isVideo(getFileExtension(file.name))">
                <video :src="file.dataURL ? file.dataURL : file.src" muted controls class="img-fluid" :type="file.type"></video>
              </div>
              <div class="col" v-else>
                <div class="p-5">
                  {{ $t('surveyQuestionScreen.takeAPicture.noPreviewAvailable') }}
                </div>
              </div>
            </div>
            <div class="row m-0 image-name-button" :class="{ 'deleted-Style': file.deleted, 'image-name-button': !viewMode, 'image-name-button-disabled': viewMode }">
              <div class="col p-4 m-0">
                <div class="py-1">
                  {{ shortenName(file.name) }}
                </div>
              </div>
              <div v-if="!viewMode" class="col-2 py-4 px-5 m-0 delete-image-button" :class="{ 'deleted-Style': file.deleted }"
                   @click="removeFile(file, index, $event)">
                <smtt-icon :icon="file.deleted ? 'close' : 'delete'" width="20px" class="align-middle"/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="modalPreviewVisible" class="modal-mask align-content-center">
      <div class="close-preview" @click="hidePreview">
        <smtt-icon icon="close" class="justify-content-center close-preview-icon" />
      </div>
      <div class="modal-wrapper">
        <div class="modal-container scrollable">
          <video v-if="previewType === 'video'" controls class="img-fluid mx-auto d-block image-view">
            <source :src="previewSource" />
          </video>
          <img v-if="previewType === 'image'" v-lazy="previewSource" class="img-fluid mx-auto d-block image-view" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
global.jQuery = require('jquery');
const $ = global.jQuery;
window.$ = $;
import generateString from '../../../utils/randomStringGenerator';
import AppButton from '@/components/AppButton';

export default {
  'name': 'TakePicture',
  components: { AppButton },
  'props': {
    'selectedImages': {
      'type': Array,
      'required': false,
      'default': null
    },
    'formats': {
      'type': Array,
      'required': false,
      'default': null
    },
    'viewMode': {
      'type': Boolean,
      'required': false
    }
  },
  data() {
    return {
      'converted': [],
      'modalPreviewVisible': false,
      // based on the type of the file to preview, use a <img> tag for 'image', or <video> for 'video'
      'previewType': '',
      'previewSource': '#',
      'showInputTypeSelector': false,
      'showUploadVideoOption': false,
      'showUploadPictureOption': false,
      'showUploadFileOption': false,
    }
  },
  watch: {
    selectedImages(newValue) {
      this.converted = newValue;
    }
  },
  'computed': {
    'lowerCaseFormats': function () {
      const formats = []
      if (this.formats !== null) {
        // If the format is an array of empty string, we allow all types of formats
        if (this.formats.length === 1 && this.formats[0] === '') {
          return formats;
        }

        for (let i = 0; i < this.formats.length; i++) {
          if (this.formats[i].toLowerCase() === 'jpg') {
            formats.push('jpeg');
          }
          formats.push(this.formats[i].toLowerCase())
        }
      }
      return formats
    }
  },
  'methods': {
    'getFileExtension': function (filename) {
      return (filename.substring(filename.lastIndexOf('.') + 1, filename.length) || filename).toLowerCase();
    },
    clickFileInput() {
      if (!this.viewMode) {
        this.showInputTypeSelector = !this.showInputTypeSelector;
      }
    },
    captureVideo() {
      if (!this.viewMode) {
        $(this.$refs.fileInputVideo).click()
      }
    },
    captureImage() {
      if (!this.viewMode) {
        $(this.$refs.fileInputImage).click()
      }
    },
    captureFile() {
      if (!this.viewMode) {
        $(this.$refs.fileInputAny).click()
      }
    },
    'toBase64': (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    }),
    'toBlob': (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e) => {
        const blob = new Blob([new Uint8Array(e.target.result)], {type: file.type});
        resolve(blob);
      }
      reader.onerror = (error) => reject(error);
    }),
    async encodeSingleFileToBase64(file) {
      return {
        'name': file.name,
        'base64': await this.toBase64(file),
        'dataURL': window.webkitURL
          ? window.webkitURL.createObjectURL(await this.toBlob(file))
          : URL.createObjectURL(await this.toBlob(file)),
        'type': file.type,
        'tempId': generateString(20),
        'deleted': false,
      };
    },
    async addFiles(e) {
      let files = e.target.files

      if (files.length > 0) {
        let encodedFiles = []

        for(let file of files) {
          let fileName = file.name.toLowerCase()

          // If there is any format restriction, we assume all formats are valid.
          if (this.lowerCaseFormats.length === 0 || this.lowerCaseFormats.includes(this.getFileExtension(fileName))) {
            const encodedFile = await this.encodeSingleFileToBase64(file)

            encodedFiles.push(encodedFile);
          }
        }

        if(encodedFiles.length > 0) {
          this.converted = this.converted.concat(encodedFiles)

          this.answered();
        }
      }
    },
    removeFile(file, index, e) {
      e.stopPropagation()
      if (!this.viewMode) {
        // eslint-disable-next-line prefer-destructuring
        const fileNameRow = this.$refs[`fileNameRow-${file.name}`][0]

        if (this.converted[index].deleted === true) {
          this.converted[index].deleted = false;

          $(fileNameRow).removeClass('restore-image-button');
        } else {
          this.converted[index].deleted = true;

          $(fileNameRow).addClass('restore-image-button');
        }

        this.$forceUpdate()

        this.answered();
      }
    },
    shortenName(fileName) {
      if (fileName.length > 25) {
        return `${fileName.substr(0, 23)}...`
      } else {
        return fileName
      }
    },
    answered() {
      this.$emit('onAnswered', this.converted);
    },
    showPreview(file) {
      const extension = this.getFileExtension(file.name).toLowerCase();
      if (!file.deleted) {
        if (this.isVideo(extension)) {
          this.previewVideo(file, extension);
        }
        if (this.isImage(extension)) {
          this.previewImage(file, extension);
        }
      }
    },
    isImage(extension) {
      return ['jpg', 'png', 'gif', 'jpeg'].includes(extension);
    },
    isVideo(extension) {
      return ['mp4', 'avi', 'mov'].includes(extension);
    },
    previewImage(file, extension) {
      // Enable preview with mode image
      this.previewType = 'image';
      if (file.base64) {
        const metadataDelimiter = file.base64.search(',');
        if (extension === 'jpg') {
          this.previewSource = `data:image/jpg;base64${file.base64.slice(metadataDelimiter)}`;
        } else if (extension === 'jpeg') {
          this.previewSource = `data:image/jpeg;base64${file.base64.slice(metadataDelimiter)}`;
        } else if (extension === 'png') {
          this.previewSource = `data:image/png;base64${file.base64.slice(metadataDelimiter)}`;
        } else if (extension === 'gif') {
          this.previewSource = `data:image/gif;base64${file.base64.slice(metadataDelimiter)}`;
        }
      } else if (file.src) {
        this.previewSource = file.src
      }
      // Show modal
      this.modalPreviewVisible = true;
    },
    previewVideo(file, extension) {
      // Workaround: override the metadata from base64 for manual metadata because the backend doesn't return
      // the proper metadata in the base64

      // Enable preview with mode video
      this.previewType = 'video';
      if (file.base64) {
        const metadataDelimiter = file.base64.search(',');
        if (extension === 'mp4') {
          this.previewSource = `data:video/mp4;base64${file.base64.slice(metadataDelimiter)}`;
        } else if (extension === 'avi') {
          this.previewSource = `data:video/vnd.avi;base64${file.base64.slice(metadataDelimiter)}`;
        } else if (extension === 'mov') {
          this.previewSource = `data:video/quicktime;base64${file.base64.slice(metadataDelimiter)}`;
        }
      } else if (file.src) {
        this.previewSource = file.src
      }
    },
    hidePreview() {
      this.modalPreviewVisible = false;
      this.previewSource = '#';
    },
  },
  created: function() {
    if (this.selectedImages?.length > 0) {
      this.converted = this.selectedImages.map((item) => {
        return {...item, deleted: false}
      });
    }

    if (this.formats !== null) {
      // If the format is an array of empty string, we enable all buttons
      if (this.formats.length === 1 && this.formats[0] === '') {
        this.showUploadVideoOption = true;
        this.showUploadPictureOption = true;
        this.showUploadFileOption = true;
      }

      for (let i = 0; i < this.formats.length; i++) {
        if (['png', 'jpg', 'jpeg', 'gif','heic', 'heif'].includes(this.formats[i].toLowerCase())) {
          this.showUploadPictureOption = true;
        }

        if (this.formats[i].toLowerCase() === 'pdf') {
          this.showUploadFileOption = true;
        }

        if (['mov', 'avi', 'mp4'].includes(this.formats[i].toLowerCase())) {
          this.showUploadVideoOption = true;
        }
      }
    } else {
      this.showUploadVideoOption = true;
      this.showUploadPictureOption = true;
      this.showUploadFileOption = true;
    }
  }
}
</script>

<style scoped>
.take-picture {
  cursor: pointer;
}

.image-item {
  color: rgb(79, 35, 201);
  border-radius: 4px;
  border: solid 2px rgb(79, 35,201);
  background-color: transparent;
}

.image-item-disabled {
  color: #ababb5;
  border-radius: 4px;
  border: solid 2px #ababb5;
  background-color: transparent;
}

.image-preview-button {
  color: rgb(79, 35, 201);
  background-color: rgb(238, 233, 251);
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.image-name-button {
  color: rgb(79, 35, 201);
  background-color: rgb(238, 233, 251);
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.image-name-button-disabled {
  color: #495057;
  background-color: #e9ecef;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.deleted-Style {
  color: gray !important;
  border-color: gray !important;
  background: white !important;
}

.modal-mask {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  transition: opacity 0.3s ease;
  display: table;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-container {
  margin: 0 16px;
  transition: all 0.3s ease;
  z-index: 1000;
}

.scrollable {
  overflow: auto;
  max-height: 90vh;
}

.image-view {
  border-radius: 4px;
}

.close-preview {
  position:fixed;
  float: top;
  background-color: rgba(0,0,0,0.5);
  border-radius: 4px;
  width: 40px;
  height: 40px;
  margin: 2px 0 0 2px;
}

.close-preview-icon {
  color: white;
  width:40px;
  height:40px;
}
</style>
